import { ILayoutBaseTestGen } from '../layoutBaseTest';
import {
  PdfGen,
  PdfFont,
  PdfRect,
  PdfBrush,
  ETextAlign,
  PdfUnit,
  PdfColor,
  PdfPen,
  PageGen,
} from '../../pdf/pdf';
import '../../pdf/draw/string';
import '../../pdf/draw/primitives';
import { moduleRegistry } from '../../modules';
import { smartModule } from '../../modules/smart';
import { OptsBaseTest } from '../optsBaseTest';

const MM = PdfUnit.FromMillimeters;
const MARGIN = MM(12);

// Two-column layout on A4 portrait (210 × 297 mm)
const COL1_X = MARGIN;
const COL2_X = MM(111);
const ITEM_W = MM(87);
const ITEM_H = MM(110);
const ITEM_H_SM = MM(80);

// 2-row layout (h = 110 mm each)
const ROW1_Y = MM(18);
const ROW2_Y = MM(145);

// 3-row layout (h = 80 mm each)
const R1_Y = MM(18);
const R2_Y = MM(112);
const R3_Y = MM(206);

export class TestSmart implements ILayoutBaseTestGen {
  async generate(pdf: PdfGen, _opts: OptsBaseTest): Promise<void> {
    await pdf.addStandardFonts();

    moduleRegistry.register(smartModule);
    const smart = moduleRegistry.get('smart');
    if (!smart) throw new Error('Smart module not found');

    const font = new PdfFont(PdfFont.FONT_NAME, 13);
    const smallFont = new PdfFont(PdfFont.FONT_NAME, 10);
    const outline = new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5);

    // Helper: draw label above, outline rect, then smart content
    const ds = (
      page: PageGen,
      x: number,
      y: number,
      w: number,
      h: number,
      label: string,
      data: string,
      options: Record<string, string>
    ) => {
      page.drawString(
        label,
        smallFont,
        PdfBrush.Black,
        null,
        new PdfRect(x, y - MM(5), w, MM(5)),
        ETextAlign.CenterLeft
      );
      const r = new PdfRect(x, y, w, h);
      page.drawRectangle(outline, null, r);
      smart.draw(smart.parse(data, options), r, ETextAlign.CenterLeft, { font, page });
    };

    const title = (page: PageGen, text: string) =>
      page.drawString(
        text,
        font,
        PdfBrush.Black,
        null,
        new PdfRect(MARGIN, MM(5), MM(186), MM(10)),
        ETextAlign.CenterLeft
      );

    // ── Page 1: List ──────────────────────────────────────────────────────────
    const p1 = pdf.addPage();
    title(p1, 'SmartArt — List');

    ds(
      p1,
      COL1_X,
      ROW1_Y,
      ITEM_W,
      ITEM_H,
      '1. Nested list (soft):',
      `- Planning
  - Define goals
  - Assign roles
- Execution
  - Implement
  - Write tests
- Review
  - Code review
  - QA testing`,
      { type: 'list', palette: 'soft' }
    );

    ds(
      p1,
      COL2_X,
      ROW1_Y,
      ITEM_W,
      ITEM_H,
      '2. Deep list (pastelStrong):',
      `- Animals
  - Mammals
    - Dog
    - Cat
  - Birds
    - Eagle
    - Penguin
  - Reptiles
    - Snake`,
      { type: 'list', palette: 'pastelStrong' }
    );

    ds(
      p1,
      COL1_X,
      ROW2_Y,
      ITEM_W,
      ITEM_H_SM,
      '3. Flat list (deep):',
      `- Mathematics
- Science
- History
- Literature
- Geography
- Art`,
      { type: 'list', palette: 'deep' }
    );

    ds(
      p1,
      COL2_X,
      ROW2_Y,
      ITEM_W,
      ITEM_H_SM,
      '4. Two-level list (warmLight):',
      `- Frontend
  - React
  - Vue
- Backend
  - Node.js
  - Python
- Database
  - PostgreSQL
  - MongoDB`,
      { type: 'list', palette: 'warmLight' }
    );

    // ── Page 2: Process ───────────────────────────────────────────────────────
    const p2 = pdf.addPage();
    title(p2, 'SmartArt — Process');

    ds(
      p2,
      COL1_X,
      R1_Y,
      ITEM_W,
      ITEM_H_SM,
      '1. 3-step process (soft):',
      `- Research
  - Gather data
  - Interview users
- Design
  - Wireframes
  - Prototypes
- Launch
  - Deploy
  - Monitor`,
      { type: 'process', palette: 'soft' }
    );

    ds(
      p2,
      COL2_X,
      R1_Y,
      ITEM_W,
      ITEM_H_SM,
      '2. 4-step process (pastelStrong):',
      `- Idea
- Plan
- Build
- Ship`,
      { type: 'process', palette: 'pastelStrong' }
    );

    ds(
      p2,
      COL1_X,
      R2_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H_SM,
      '3. 5-step workflow (deep):',
      `- Collect
  - Forms
  - Surveys
- Analyse
  - Statistics
  - Trends
- Report
  - Dashboard
  - Export
- Decide
  - Committee
  - Vote
- Act
  - Assign tasks
  - Track`,
      { type: 'process', palette: 'deep' }
    );

    ds(
      p2,
      COL1_X,
      R3_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H_SM,
      '4. 3-step process (warmLight):',
      `- Discover
  - Market research
  - Competitive analysis
  - User interviews
- Define
  - Problem statement
  - Success metrics
  - Constraints
- Deliver
  - MVP release
  - Feedback loop
  - Iteration`,
      { type: 'process', palette: 'warmLight' }
    );

    // ── Page 3: Hierarchy ─────────────────────────────────────────────────────
    const p3 = pdf.addPage();
    title(p3, 'SmartArt — Hierarchy');

    ds(
      p3,
      COL1_X,
      R1_Y,
      ITEM_W,
      ITEM_H_SM,
      '1. Org chart (soft):',
      `- CEO
  - CTO
    - Dev Lead
    - QA Lead
  - CFO
    - Accountant
  - CMO
    - Designer`,
      { type: 'hierarchy', palette: 'soft' }
    );

    ds(
      p3,
      COL2_X,
      R1_Y,
      ITEM_W,
      ITEM_H_SM,
      '2. File system (pastelStrong):',
      `- Root
  - src
    - components
    - pages
  - tests
    - unit
    - e2e
  - docs`,
      { type: 'hierarchy', palette: 'pastelStrong' }
    );

    ds(
      p3,
      COL1_X,
      R2_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H_SM,
      '3. Category tree (deep):',
      `- Products
  - Electronics
    - Phones
    - Laptops
  - Clothing
    - Men
    - Women
  - Food
    - Fresh
    - Packaged`,
      { type: 'hierarchy', palette: 'deep' }
    );

    ds(
      p3,
      COL1_X,
      R3_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H_SM,
      '4. Subject tree (warmLight):',
      `- Sciences
  - Physics
  - Chemistry
  - Biology
- Humanities
  - History
  - Philosophy
- Arts
  - Music
  - Visual Arts`,
      { type: 'hierarchy', palette: 'warmLight' }
    );

    // ── Page 4: Cycle ─────────────────────────────────────────────────────────
    const p4 = pdf.addPage();
    title(p4, 'SmartArt — Cycle');

    ds(
      p4,
      COL1_X,
      R1_Y,
      ITEM_W,
      ITEM_H_SM,
      '1. PDCA (soft):',
      `- Plan
- Do
- Check
- Act`,
      { type: 'cycle', palette: 'soft' }
    );

    ds(
      p4,
      COL2_X,
      R1_Y,
      ITEM_W,
      ITEM_H_SM,
      '2. Agile sprint (pastelStrong):',
      `- Backlog
- Sprint Plan
- Dev & Test
- Review
- Retrospective`,
      { type: 'cycle', palette: 'pastelStrong' }
    );

    ds(
      p4,
      COL1_X,
      R2_Y,
      ITEM_W,
      ITEM_H_SM,
      '3. Water cycle (deep):',
      `- Evaporation
- Condensation
- Precipitation
- Runoff`,
      { type: 'cycle', palette: 'deep' }
    );

    ds(
      p4,
      COL2_X,
      R2_Y,
      ITEM_W,
      ITEM_H_SM,
      '4. Three-step loop (warmLight):',
      `- Input
- Process
- Output`,
      { type: 'cycle', palette: 'warmLight' }
    );

    ds(
      p4,
      COL1_X,
      R3_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H_SM,
      '5. Six-stage cycle (professional):',
      `- Identify
- Analyse
- Design
- Implement
- Evaluate
- Improve`,
      { type: 'cycle', palette: 'professional' }
    );

    // ── Page 5: Pyramid ───────────────────────────────────────────────────────
    const p5 = pdf.addPage();
    title(p5, 'SmartArt — Pyramid');

    ds(
      p5,
      COL1_X,
      R1_Y,
      ITEM_W,
      ITEM_H_SM,
      "1. Maslow's hierarchy (soft):",
      `- Physiological
  - Food
  - Sleep
- Safety
  - Home
  - Health
- Love & Belonging
- Esteem
- Self-Actualisation`,
      { type: 'pyramid', palette: 'soft' }
    );

    ds(
      p5,
      COL2_X,
      R1_Y,
      ITEM_W,
      ITEM_H_SM,
      '2. Business priority (pastelStrong):',
      `- Vision
- Strategy
  - Goals
- Tactics
  - Projects
  - Tasks
- Execution`,
      { type: 'pyramid', palette: 'pastelStrong' }
    );

    ds(
      p5,
      COL1_X,
      R2_Y,
      ITEM_W,
      ITEM_H_SM,
      '3. Knowledge levels (deep):',
      `- Data
- Information
- Knowledge
- Wisdom`,
      { type: 'pyramid', palette: 'deep' }
    );

    ds(
      p5,
      COL2_X,
      R2_Y,
      ITEM_W,
      ITEM_H_SM,
      '4. Risk management (warmLight):',
      `- Critical risks
- High risks
- Medium risks
- Low risks`,
      { type: 'pyramid', palette: 'warmLight' }
    );

    ds(
      p5,
      COL1_X,
      R3_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H_SM,
      '5. Bloom taxonomy (professional):',
      `- Create
  - Design
  - Construct
- Evaluate
- Analyse
- Apply
- Understand
- Remember`,
      { type: 'pyramid', palette: 'professional' }
    );

    // ── Page 6: Sandwich ──────────────────────────────────────────────────────
    const p6 = pdf.addPage();
    title(p6, 'SmartArt — Sandwich');

    ds(
      p6,
      COL1_X,
      R1_Y,
      ITEM_W,
      ITEM_H_SM,
      '1. Simple bands (soft):',
      `- Presentation Layer
- Business Logic
- Data Access
- Database`,
      { type: 'sandwich', palette: 'soft' }
    );

    ds(
      p6,
      COL2_X,
      R1_Y,
      ITEM_W,
      ITEM_H_SM,
      '2. With pills (pastelStrong):',
      `- Frontend
  - React
  - CSS
  - Tests
- API Layer
  - REST
  - Auth
  - Logs
- Backend
  - Services
  - Jobs
  - Cache
- Storage
  - Postgres
  - Redis
  - S3`,
      { type: 'sandwich', palette: 'pastelStrong' }
    );

    ds(
      p6,
      COL1_X,
      R2_Y,
      ITEM_W,
      ITEM_H_SM,
      '3. OSI model (deep):',
      `- Application
- Presentation
- Session
- Transport
- Network
- Data Link
- Physical`,
      { type: 'sandwich', palette: 'deep' }
    );

    ds(
      p6,
      COL2_X,
      R2_Y,
      ITEM_W,
      ITEM_H_SM,
      '4. Burger layers (warmLight):',
      `- Top Bun
- Lettuce & Tomato
- Cheese
- Patty
- Sauce
- Bottom Bun`,
      { type: 'sandwich', palette: 'warmLight' }
    );

    ds(
      p6,
      COL1_X,
      R3_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H_SM,
      '5. Project phases with deliverables (professional):',
      `- Initiation
  - Charter
  - Stakeholders
  - Kickoff
- Planning
  - Scope
  - Schedule
  - Budget
- Execution
  - Build
  - Test
  - Review
- Closure
  - Handover
  - Lessons
  - Archive`,
      { type: 'sandwich', palette: 'professional' }
    );

    // ── Page 7: Staircase ─────────────────────────────────────────────────────
    const p7 = pdf.addPage();
    title(p7, 'SmartArt — Staircase');

    ds(
      p7,
      COL1_X,
      R1_Y,
      ITEM_W,
      ITEM_H_SM,
      '1. 4-step growth (soft):',
      `- Beginner
- Learner
- Practitioner
- Expert`,
      { type: 'staircase', palette: 'soft' }
    );

    ds(
      p7,
      COL2_X,
      R1_Y,
      ITEM_W,
      ITEM_H_SM,
      '2. 5-step career (pastelStrong):',
      `- Intern
- Junior
- Mid-level
- Senior
- Principal`,
      { type: 'staircase', palette: 'pastelStrong' }
    );

    ds(
      p7,
      COL1_X,
      R2_Y,
      ITEM_W,
      ITEM_H_SM,
      '3. Value steps (deep):',
      `- Cost reduction
- Efficiency
- Quality
- Innovation
- Leadership
- Disruption`,
      { type: 'staircase', palette: 'deep' }
    );

    ds(
      p7,
      COL2_X,
      R2_Y,
      ITEM_W,
      ITEM_H_SM,
      '4. Bloom taxonomy ascending (warmLight):',
      `- Remember
- Understand
- Apply
- Analyse
- Evaluate
- Create`,
      { type: 'staircase', palette: 'warmLight' }
    );

    ds(
      p7,
      COL1_X,
      R3_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H_SM,
      '5. Product maturity (professional):',
      `- Concept
- Prototype
- Alpha
- Beta
- Launch
- Growth
- Maturity`,
      { type: 'staircase', palette: 'professional' }
    );

    // ── Page 8: Matrix ────────────────────────────────────────────────────────
    const p8 = pdf.addPage();
    title(p8, 'SmartArt — Matrix');

    ds(
      p8,
      COL1_X,
      R1_Y,
      ITEM_W,
      ITEM_H_SM,
      '1. 2×2 Eisenhower (soft):',
      `- Urgent & Important
  - Do now
  - Deadlines
- Not Urgent & Important
  - Plan
  - Strategy
- Urgent & Not Important
  - Delegate
  - Meetings
- Not Urgent & Not Important
  - Eliminate
  - Time wasters`,
      { type: 'matrix', palette: 'soft', cols: '2' }
    );

    ds(
      p8,
      COL2_X,
      R1_Y,
      ITEM_W,
      ITEM_H_SM,
      '2. 2×2 BCG Matrix (pastelStrong):',
      `- Stars
  - High share
  - High growth
- Question Marks
  - Low share
  - High growth
- Cash Cows
  - High share
  - Low growth
- Dogs
  - Low share
  - Low growth`,
      { type: 'matrix', palette: 'pastelStrong', cols: '2' }
    );

    ds(
      p8,
      COL1_X,
      R2_Y,
      ITEM_W,
      ITEM_H_SM,
      '3. 2×2 SWOT (deep):',
      `- Strengths
  - Brand
  - Team
  - Tech
- Weaknesses
  - Budget
  - Reach
  - Scale
- Opportunities
  - New markets
  - Partnerships
- Threats
  - Competition
  - Regulation`,
      { type: 'matrix', palette: 'deep', cols: '2' }
    );

    ds(
      p8,
      COL2_X,
      R2_Y,
      ITEM_W,
      ITEM_H_SM,
      '4. 3-column skills (warmLight):',
      `- Technical
  - Coding
  - Architecture
  - Testing
- Leadership
  - Vision
  - Communication
  - Delegation
- Business
  - Strategy
  - Finance
  - Marketing`,
      { type: 'matrix', palette: 'warmLight', cols: '3' }
    );

    ds(
      p8,
      COL1_X,
      R3_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H_SM,
      '5. 4-column roadmap (professional):',
      `- Q1
  - Foundation
  - Research
- Q2
  - Build MVP
  - Alpha test
- Q3
  - Beta launch
  - Iterate
- Q4
  - Full launch
  - Scale`,
      { type: 'matrix', palette: 'professional', cols: '4' }
    );

    // ── Page 9: Onion / Target ────────────────────────────────────────────────
    const p9 = pdf.addPage();
    title(p9, 'SmartArt — Onion & Target');

    ds(
      p9,
      COL1_X,
      R1_Y,
      ITEM_W,
      ITEM_H_SM,
      '1. Onion: environment (soft):',
      `- Global context
- Industry
- Company
- Team
- Individual`,
      { type: 'onion', palette: 'soft' }
    );

    ds(
      p9,
      COL2_X,
      R1_Y,
      ITEM_W,
      ITEM_H_SM,
      '2. Target: priority (pastelStrong):',
      `- Must have
- Should have
- Could have
- Won't have`,
      { type: 'target', palette: 'pastelStrong' }
    );

    ds(
      p9,
      COL1_X,
      R2_Y,
      ITEM_W,
      ITEM_H_SM,
      '3. Onion: learning (deep):',
      `- Awareness
- Understanding
- Application
- Mastery`,
      { type: 'onion', palette: 'deep' }
    );

    ds(
      p9,
      COL2_X,
      R2_Y,
      ITEM_W,
      ITEM_H_SM,
      '4. Target: risk levels (warmLight):',
      `- Critical
- High
- Medium
- Low
- Negligible`,
      { type: 'target', palette: 'warmLight' }
    );

    ds(
      p9,
      COL1_X,
      R3_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H_SM,
      '5. Onion: security layers (professional):',
      `- Internet perimeter
- DMZ / Firewall
- Network segmentation
- Host hardening
- Application security
- Data encryption`,
      { type: 'onion', palette: 'professional' }
    );

    // ── Page 10: Spoke Wheel ──────────────────────────────────────────────────
    const p10 = pdf.addPage();
    title(p10, 'SmartArt — Spoke Wheel');

    ds(
      p10,
      COL1_X,
      R1_Y,
      ITEM_W,
      ITEM_H_SM,
      '1. Marketing mix (soft):',
      `- Product
- Price
- Place
- Promotion
- People
- Process`,
      { type: 'spoke-wheel', palette: 'soft' }
    );

    ds(
      p10,
      COL2_X,
      R1_Y,
      ITEM_W,
      ITEM_H_SM,
      '2. Hub with children (pastelStrong):',
      `- Platform
- Mobile
  - iOS
  - Android
- Web
  - React
  - Next
- API
  - REST
  - GraphQL
- DevOps
  - CI/CD
  - Cloud`,
      { type: 'spoke-wheel', palette: 'pastelStrong' }
    );

    ds(
      p10,
      COL1_X,
      R2_Y,
      ITEM_W,
      ITEM_H_SM,
      '3. Core skills (deep):',
      `- Leadership
- Communication
- Problem Solving
- Creativity
- Collaboration
- Adaptability`,
      { type: 'spoke-wheel', palette: 'deep' }
    );

    ds(
      p10,
      COL2_X,
      R2_Y,
      ITEM_W,
      ITEM_H_SM,
      '4. Business functions (warmLight):',
      `- Operations
- Sales
- Marketing
- Finance
- HR
- Legal
- Technology`,
      { type: 'spoke-wheel', palette: 'warmLight' }
    );

    ds(
      p10,
      COL1_X,
      R3_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H_SM,
      '5. Tech ecosystem (professional):',
      `- Cloud
- Security
- Data
- AI/ML
- DevOps
- Frontend
- Backend
- Mobile`,
      { type: 'spoke-wheel', palette: 'professional' }
    );

    // ── Page 11: Timeline Vertical ────────────────────────────────────────────
    const p11 = pdf.addPage();
    title(p11, 'SmartArt — Timeline Vertical');

    ds(
      p11,
      COL1_X,
      R1_Y,
      ITEM_W,
      ITEM_H_SM,
      '1. Project milestones (soft):',
      `- Kickoff
  - Team assembled
  - Scope agreed
- Design
  - Wireframes approved
  - Tech stack chosen
- Build
  - MVP ready
  - Tests passing
- Launch
  - Go live
  - Monitoring on`,
      { type: 'timeline-vertical', palette: 'soft' }
    );

    ds(
      p11,
      COL2_X,
      R1_Y,
      ITEM_W,
      ITEM_H_SM,
      '2. Historical events (pastelStrong):',
      `- 1990
- 1995
- 2000
- 2005
- 2010
- 2015
- 2020`,
      { type: 'timeline-vertical', palette: 'pastelStrong' }
    );

    ds(
      p11,
      COL1_X,
      R2_Y,
      ITEM_W,
      ITEM_H_SM,
      '3. Product roadmap (deep):',
      `- Research
  - User interviews
  - Competitor audit
- Alpha
  - Core features
  - Internal testing
- Beta
  - Public testing
  - Bug fixes
- v1.0
  - Full release
  - Marketing campaign`,
      { type: 'timeline-vertical', palette: 'deep' }
    );

    ds(
      p11,
      COL2_X,
      R2_Y,
      ITEM_W,
      ITEM_H_SM,
      '4. Learning path (warmLight):',
      `- Fundamentals
  - Syntax
  - Types
- Intermediate
  - Patterns
  - Testing
- Advanced
  - Architecture
  - Performance
- Expert
  - Mentoring
  - OSS`,
      { type: 'timeline-vertical', palette: 'warmLight' }
    );

    ds(
      p11,
      COL1_X,
      R3_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H_SM,
      '5. Company history (professional):',
      `- Founded
  - 5 employees
  - Seed funding
- Series A
  - 50 employees
  - First product
- Expansion
  - 200 employees
  - 3 offices
- IPO
  - Public company
  - Global reach`,
      { type: 'timeline-vertical', palette: 'professional' }
    );

    // ── Page 12: Fishbone ─────────────────────────────────────────────────────
    const p12 = pdf.addPage();
    title(p12, 'SmartArt — Fishbone (Ishikawa)');

    ds(
      p12,
      COL1_X,
      R1_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H_SM,
      '1. Software bug causes (soft):',
      `- Code quality
  - No reviews
  - Tech debt
- Testing
  - No unit tests
  - Manual only
- Process
  - No spec
  - Rushed
- Environment
  - Config drift
  - Stale deps
- Production bug`,
      { type: 'fishbone', palette: 'soft' }
    );

    ds(
      p12,
      COL1_X,
      R2_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H_SM,
      '2. Project delay causes (pastelStrong):',
      `- People
  - Understaffed
  - Sick leave
- Requirements
  - Scope creep
  - Late changes
- Technology
  - Integration issues
  - Legacy system
- Communication
  - Silos
  - Missing info
- Project delivered late`,
      { type: 'fishbone', palette: 'pastelStrong' }
    );

    ds(
      p12,
      COL1_X,
      R3_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H_SM,
      '3. Poor performance causes (deep):',
      `- Hardware
  - Slow disks
  - Low RAM
- Database
  - No indexes
  - N+1 queries
- Code
  - Sync I/O
  - Memory leaks
- Network
  - High latency
  - Packet loss
- Slow response`,
      { type: 'fishbone', palette: 'deep' }
    );

    // ── Page 13: Flowchart ────────────────────────────────────────────────────
    const p13 = pdf.addPage();
    title(p13, 'SmartArt — Flowchart');

    ds(
      p13,
      COL1_X,
      R1_Y,
      ITEM_W,
      ITEM_H_SM,
      '1. Login flow (soft):',
      `- Enter credentials
- ? Valid credentials?
  - Yes
  - No → retry
- Load dashboard
- Log session
- Show user profile`,
      { type: 'flowchart', palette: 'soft' }
    );

    ds(
      p13,
      COL2_X,
      R1_Y,
      ITEM_W,
      ITEM_H_SM,
      '2. Order processing (pastelStrong):',
      `- Receive order
- ? In stock?
  - Yes
  - No → notify
- Process payment
- ? Payment OK?
  - Yes
  - No → cancel
- Ship order
- Send confirmation`,
      { type: 'flowchart', palette: 'pastelStrong' }
    );

    ds(
      p13,
      COL1_X,
      R2_Y,
      ITEM_W,
      ITEM_H_SM,
      '3. CI/CD pipeline (deep):',
      `- Push code
- Run tests
- ? Tests pass?
  - Yes
  - No → fix
- Build artefact
- Deploy to staging
- ? Smoke test OK?
  - Pass
  - Fail
- Deploy to prod`,
      { type: 'flowchart', palette: 'deep' }
    );

    ds(
      p13,
      COL2_X,
      R2_Y,
      ITEM_W,
      ITEM_H_SM,
      '4. Support ticket (warmLight):',
      `- Ticket received
- ? Critical?
  - Yes → escalate
  - No
- Assign agent
- Investigate
- ? Resolved?
  - Yes
  - No → escalate
- Close ticket`,
      { type: 'flowchart', palette: 'warmLight' }
    );

    ds(
      p13,
      COL1_X,
      R3_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H_SM,
      '5. Hiring process (professional):',
      `- Application received
- ? Meets criteria?
  - Yes
  - No → reject
- Phone screen
- ? Passed?
  - Yes
  - No → reject
- Technical interview
- Offer`,
      { type: 'flowchart', palette: 'professional' }
    );

    // ── Page 14: Venn ─────────────────────────────────────────────────────────
    const p14 = pdf.addPage();
    title(p14, 'SmartArt — Venn Diagram');

    ds(
      p14,
      COL1_X,
      R1_Y,
      ITEM_W,
      ITEM_H_SM,
      '1. Frontend vs Backend (soft):',
      `- Frontend
  - React
  - CSS
  - UX
- Full-stack
  - REST APIs
  - Auth
  - Testing
- Backend
  - Databases
  - Servers
  - DevOps`,
      { type: 'venn', palette: 'soft' }
    );

    ds(
      p14,
      COL2_X,
      R1_Y,
      ITEM_W,
      ITEM_H_SM,
      '2. Skills overlap (pastelStrong):',
      `- Design
  - Figma
  - Typography
  - Colour
- UX Design
  - Usability
  - Research
  - Prototyping
- Development
  - HTML/CSS
  - JavaScript
  - React`,
      { type: 'venn', palette: 'pastelStrong' }
    );

    ds(
      p14,
      COL1_X,
      R2_Y,
      ITEM_W,
      ITEM_H_SM,
      '3. DevOps overlap (deep):',
      `- Development
  - Code
  - Testing
  - Reviews
- DevOps
  - CI/CD
  - Monitoring
  - Automation
- Operations
  - Infra
  - Security
  - Capacity`,
      { type: 'venn', palette: 'deep' }
    );

    ds(
      p14,
      COL2_X,
      R2_Y,
      ITEM_W,
      ITEM_H_SM,
      '4. Product vs Engineering (warmLight):',
      `- Product
  - Roadmap
  - Prioritisation
  - Metrics
- Both
  - Sprint planning
  - Specs
  - Demos
- Engineering
  - Architecture
  - Code quality
  - Performance`,
      { type: 'venn', palette: 'warmLight' }
    );

    ds(
      p14,
      COL1_X,
      R3_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H_SM,
      '5. Science vs Art overlap (professional):',
      `- Science
  - Experiments
  - Data
  - Hypotheses
  - Peer review
- Both
  - Creativity
  - Problem solving
  - Curiosity
  - Iteration
- Art
  - Expression
  - Aesthetics
  - Emotion
  - Interpretation`,
      { type: 'venn', palette: 'professional' }
    );

    // ── Page 15: Comparison ───────────────────────────────────────────────────
    const p15 = pdf.addPage();
    title(p15, 'SmartArt — Comparison');

    ds(
      p15,
      COL1_X,
      R1_Y,
      ITEM_W,
      ITEM_H_SM,
      '1. Option A vs B (soft):',
      `- Option A
  - Fast delivery
  - Lower cost
  - Less features
- Option B
  - Slower delivery
  - Higher cost
  - More features`,
      { type: 'comparison', palette: 'soft' }
    );

    ds(
      p15,
      COL2_X,
      R1_Y,
      ITEM_W,
      ITEM_H_SM,
      '2. React vs Vue vs Angular (pastelStrong):',
      `- React
  - Component-based
  - Large ecosystem
- Vue
  - Simpler API
  - Template syntax
- Angular
  - Full framework
  - TypeScript first`,
      { type: 'comparison', palette: 'pastelStrong' }
    );

    ds(
      p15,
      COL1_X,
      R2_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H_SM,
      '3. SWOT columns (deep):',
      `- Strengths
  - Brand
  - Strong team
  - Leader
- Weaknesses
  - High costs
  - Slow processes
- Opportunities
  - New markets
  - AI integration
- Threats
  - Competition
  - Regulation`,
      { type: 'comparison', palette: 'deep' }
    );

    ds(
      p15,
      COL1_X,
      R3_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H_SM,
      '4. Delivery models (warmLight):',
      `- On-Premise
  - Full control
  - High upfront
  - Manual updates
- SaaS
  - No infra
  - Subscription
  - Auto updates
- Hybrid
  - Flexible
  - Mixed cost
  - Custom control`,
      { type: 'comparison', palette: 'warmLight' }
    );

    // ── Page 16: Double Bubble ────────────────────────────────────────────────
    const p16 = pdf.addPage();
    title(p16, 'SmartArt — Double Bubble');

    ds(
      p16,
      COL1_X,
      R1_Y,
      ITEM_W,
      ITEM_H_SM,
      '1. Dog vs Cat (soft):',
      `- Dog
  - Loyal
  - Trainable
  - Active
- Cat
  - Independent
  - Quiet
  - Low effort`,
      { type: 'double-bubble', palette: 'soft' }
    );

    ds(
      p16,
      COL2_X,
      R1_Y,
      ITEM_W,
      ITEM_H_SM,
      '2. React vs Vue (pastelStrong):',
      `- React
  - JSX
  - Hooks
  - Large ecosystem
- Vue
  - Templates
  - Options API
  - Smaller bundle`,
      { type: 'double-bubble', palette: 'pastelStrong' }
    );

    ds(
      p16,
      COL1_X,
      R2_Y,
      ITEM_W,
      ITEM_H_SM,
      '3. Book vs Film (deep):',
      `- Book
  - Imagination
  - Depth
  - Own pace
  - Inner thoughts
- Film
  - Visual
  - Concise
  - Shared exp
  - Score & effects`,
      { type: 'double-bubble', palette: 'deep' }
    );

    ds(
      p16,
      COL2_X,
      R2_Y,
      ITEM_W,
      ITEM_H_SM,
      '4. Remote vs Office (warmLight):',
      `- Remote
  - Flexible hours
  - No commute
  - Personal space
- Office
  - Team proximity
  - Clear boundaries
  - Collaboration`,
      { type: 'double-bubble', palette: 'warmLight' }
    );

    // ── Page 17: Chevron ──────────────────────────────────────────────────────
    const p17 = pdf.addPage();
    title(p17, 'SmartArt — Chevron');

    ds(
      p17,
      COL1_X,
      R1_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H_SM,
      '1. 4-step deploy (soft):',
      `- Plan
- Build
- Test
- Deploy`,
      { type: 'chevron', palette: 'soft' }
    );

    ds(
      p17,
      COL1_X,
      R2_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H_SM,
      '2. 5-step sales (pastelStrong):',
      `- Lead
  - Outreach
- Qualify
  - Criteria
- Pitch
  - Deck
- Negotiate
  - Terms
- Close
  - Sign`,
      { type: 'chevron', palette: 'pastelStrong' }
    );

    ds(
      p17,
      COL1_X,
      R3_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H_SM,
      '3. Design thinking (deep):',
      `- Empathise
- Define
- Ideate
- Prototype
- Test`,
      { type: 'chevron', palette: 'deep' }
    );

    // ── Page 18: Inverted Pyramid ─────────────────────────────────────────────
    const p18 = pdf.addPage();
    title(p18, 'SmartArt — Inverted Pyramid');

    ds(
      p18,
      COL1_X,
      R1_Y,
      ITEM_W,
      ITEM_H_SM,
      '1. News story (soft):',
      `- Lead paragraph
  - Who, What, Where
- Supporting facts
  - Quotes
  - Data
- Background
  - Context
- Additional detail`,
      { type: 'pyramid-inverted', palette: 'soft' }
    );

    ds(
      p18,
      COL2_X,
      R1_Y,
      ITEM_W,
      ITEM_H_SM,
      '2. Audience funnel (pastelStrong):',
      `- All users
- Registered users
- Active users
- Paying customers
- Power users`,
      { type: 'pyramid-inverted', palette: 'pastelStrong' }
    );

    ds(
      p18,
      COL1_X,
      R2_Y,
      ITEM_W,
      ITEM_H_SM,
      '3. Design hierarchy (deep):',
      `- User needs
- Business goals
- Technical limits
- Design decisions`,
      { type: 'pyramid-inverted', palette: 'deep' }
    );

    ds(
      p18,
      COL2_X,
      R2_Y,
      ITEM_W,
      ITEM_H_SM,
      '4. Audience priority (warmLight):',
      `- General public
- Target segment
- Loyal customers
- Champions`,
      { type: 'pyramid-inverted', palette: 'warmLight' }
    );

    ds(
      p18,
      COL1_X,
      R3_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H_SM,
      '5. Evidence strength (professional):',
      `- Anecdotes
- Case studies
- Expert opinion
- Statistical data
- Systematic reviews
- Randomised trials`,
      { type: 'pyramid-inverted', palette: 'professional' }
    );

    // ── Page 19: Hierarchy Left ───────────────────────────────────────────────
    const p19 = pdf.addPage();
    title(p19, 'SmartArt — Hierarchy Left');

    ds(
      p19,
      COL1_X,
      R1_Y,
      ITEM_W,
      ITEM_H_SM,
      '1. Org chart (soft):',
      `- CEO
  - CTO
    - Dev Lead
    - QA Lead
  - CFO
  - CMO`,
      { type: 'hierarchy-left', palette: 'soft' }
    );

    ds(
      p19,
      COL2_X,
      R1_Y,
      ITEM_W,
      ITEM_H_SM,
      '2. File system (pastelStrong):',
      `- Root
  - src
    - components
    - utils
  - tests
  - docs`,
      { type: 'hierarchy-left', palette: 'pastelStrong' }
    );

    ds(
      p19,
      COL1_X,
      R2_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H_SM,
      '3. Taxonomy (deep):',
      `- Kingdom
  - Phylum
    - Class
      - Order
        - Family`,
      { type: 'hierarchy-left', palette: 'deep' }
    );

    ds(
      p19,
      COL1_X,
      R3_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H_SM,
      '4. Product areas (warmLight):',
      `- Platform
  - Mobile
    - iOS
    - Android
  - Web
    - Dashboard
    - Public site
  - API
    - REST
    - GraphQL`,
      { type: 'hierarchy-left', palette: 'warmLight' }
    );

    // ── Page 20: Bow-Tie ──────────────────────────────────────────────────────
    const p20 = pdf.addPage();
    title(p20, 'SmartArt — Bow-Tie');

    ds(
      p20,
      COL1_X,
      R1_Y,
      ITEM_W,
      ITEM_H_SM,
      '1. Risk bowtie (soft):',
      `- Hazard sources
  - Equipment failure
  - Human error
  - Environment
- Risk event
- Consequences
  - Injury
  - Financial loss
  - Reputation`,
      { type: 'bow-tie', palette: 'soft' }
    );

    ds(
      p20,
      COL2_X,
      R1_Y,
      ITEM_W,
      ITEM_H_SM,
      '2. Sales bowtie (pastelStrong):',
      `- Inputs
  - Leads
  - Marketing
  - Referrals
- Deal
- Outcomes
  - Revenue
  - Retention
  - Upsell`,
      { type: 'bow-tie', palette: 'pastelStrong' }
    );

    ds(
      p20,
      COL1_X,
      R2_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H_SM,
      '3. Decision analysis (deep):',
      `- Causes
  - Market shift
  - Tech disruption
  - Regulation
- Decision
- Effects
  - New product
  - Cost reduction
  - Partnership`,
      { type: 'bow-tie', palette: 'deep' }
    );

    ds(
      p20,
      COL1_X,
      R3_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H_SM,
      '4. Two-sided model (warmLight):',
      `- Inputs
  - Research
  - Budget
  - Team
- Focus point
- Outputs
  - Product
  - Launch
  - Growth`,
      { type: 'bow-tie', palette: 'warmLight' }
    );

    // ── Page 21: Gantt ────────────────────────────────────────────────────────
    const p21 = pdf.addPage();
    title(p21, 'SmartArt — Gantt');

    ds(
      p21,
      COL1_X,
      R1_Y,
      ITEM_W,
      ITEM_H_SM,
      '1. Project phases (soft):',
      `- Discovery
  - Week 1
  - Week 2
- Design
  - Week 3
  - Week 4
- Development
  - Week 5
  - Week 6
- Testing
  - Week 7
- Launch
  - Week 8`,
      { type: 'gantt', palette: 'soft' }
    );

    ds(
      p21,
      COL2_X,
      R1_Y,
      ITEM_W,
      ITEM_H_SM,
      '2. Quarterly roadmap (pastelStrong):',
      `- Q1 Foundation
  - Jan
  - Feb
  - Mar
- Q2 Build
  - Apr
  - May
  - Jun
- Q3 Launch
  - Jul
  - Aug
- Q4 Scale
  - Oct
  - Dec`,
      { type: 'gantt', palette: 'pastelStrong' }
    );

    ds(
      p21,
      COL1_X,
      R2_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H_SM,
      '3. Sprint plan (deep):',
      `- Research
- Architecture
- Core features
- Secondary features
- Testing
- Documentation
- Release`,
      { type: 'gantt', palette: 'deep' }
    );

    ds(
      p21,
      COL1_X,
      R3_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H_SM,
      '4. Event planning (warmLight):',
      `- Venue booking
  - Week 1
  - Week 2
- Invitations
  - Week 3
- Catering
  - Week 4
- A/V Setup
  - Week 5
- Rehearsal
- Event day`,
      { type: 'gantt', palette: 'warmLight' }
    );

    // ── Page 22: Swim Lane ────────────────────────────────────────────────────
    const p22 = pdf.addPage();
    title(p22, 'SmartArt — Swim Lane');

    ds(
      p22,
      COL1_X,
      R1_Y,
      ITEM_W,
      ITEM_H_SM,
      '1. Order process (soft):',
      `- Customer
  - Browse
  - Order
  - Pay
- Warehouse
  - Pick
  - Pack
  - Ship
- Support
  - Confirm
  - Track
  - Resolve`,
      { type: 'swim-lane', palette: 'soft' }
    );

    ds(
      p22,
      COL2_X,
      R1_Y,
      ITEM_W,
      ITEM_H_SM,
      '2. Dev workflow (pastelStrong):',
      `- Developer
  - Code
  - Unit test
  - PR
- Reviewer
  - Review
  - Approve
- CI/CD
  - Build
  - Deploy
- QA
  - Test
  - Sign off`,
      { type: 'swim-lane', palette: 'pastelStrong' }
    );

    ds(
      p22,
      COL1_X,
      R2_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H_SM,
      '3. HR process (deep):',
      `- Applicant
  - Apply
  - Prep
- HR
  - Screen
  - Schedule
  - Offer
- Manager
  - Interview
  - Decision
- IT
  - Setup
  - Access`,
      { type: 'swim-lane', palette: 'deep' }
    );

    ds(
      p22,
      COL1_X,
      R3_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H_SM,
      '4. Event workflow (warmLight):',
      `- Organiser
  - Plan
  - Confirm
- Marketing
  - Promote
  - Social
- Attendees
  - Register
  - Attend
- Speaker
  - Prepare
  - Present`,
      { type: 'swim-lane', palette: 'warmLight' }
    );

    // ── Page 23: Treemap ──────────────────────────────────────────────────────
    const p23 = pdf.addPage();
    title(p23, 'SmartArt — Treemap');

    ds(
      p23,
      COL1_X,
      R1_Y,
      ITEM_W,
      ITEM_H_SM,
      '1. Budget categories (soft):',
      `- Salaries
  - Engineering
  - Sales
  - Operations
- Marketing
  - Digital
  - Events
- Infrastructure
  - Cloud
  - Tools
- Admin`,
      { type: 'treemap', palette: 'soft' }
    );

    ds(
      p23,
      COL2_X,
      R1_Y,
      ITEM_W,
      ITEM_H_SM,
      '2. Website traffic (pastelStrong):',
      `- Organic
  - SEO
  - Blog
  - Video
- Paid
  - Google
  - Social
- Direct
- Referral
  - Partners
  - Press`,
      { type: 'treemap', palette: 'pastelStrong' }
    );

    ds(
      p23,
      COL1_X,
      R2_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H_SM,
      '3. Tech portfolio (deep):',
      `- Frontend
  - React
  - Next.js
  - Styles
- Backend
  - API
  - Workers
  - Auth
- Data
  - Warehouse
  - Pipelines
  - Analytics
- DevOps
  - CI/CD
  - Monitoring`,
      { type: 'treemap', palette: 'deep' }
    );

    ds(
      p23,
      COL1_X,
      R3_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H_SM,
      '4. Content types (warmLight):',
      `- Blog posts
  - Tutorials
  - Case studies
- Videos
  - Demos
  - Webinars
- Podcasts
- Social
  - LinkedIn
  - Twitter
  - Instagram`,
      { type: 'treemap', palette: 'warmLight' }
    );

    // ── Page 24: decision-tree ────────────────────────────────────────────────
    const p24 = await pdf.addPage();
    title(p24, 'SmartArt — Decision Tree');
    ds(
      p24,
      COL1_X,
      ROW1_Y,
      ITEM_W,
      ITEM_H,
      '1. Buy or rent? (coolLight):',
      `- Buy or Rent?
  - Buy
    - House
    - Condo
    - Land
  - Rent
    - Apartment
    - Studio`,
      { type: 'decision-tree', palette: 'coolLight' }
    );

    ds(
      p24,
      COL2_X,
      ROW1_Y,
      ITEM_W,
      ITEM_H,
      '2. Project path (tailwind):',
      `- Feasible?
  - Yes
    - Launch
    - Iterate
    - Scale
  - No
    - Pivot
    - Archive`,
      { type: 'decision-tree', palette: 'tailwind' }
    );

    ds(
      p24,
      COL1_X,
      ROW2_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H,
      '3. Hiring decision (soft):',
      `- Candidate Fit?
  - Strong Fit
    - Offer Now
    - Fast-track
    - Mentor Plan
  - Weak Fit
    - Reject
    - Waitlist
  - Unsure
    - 2nd Interview
    - Skills Test`,
      { type: 'decision-tree', palette: 'soft' }
    );

    // ── Page 25: funnel-wide ─────────────────────────────────────────────────
    const p25 = await pdf.addPage();
    title(p25, 'SmartArt — Funnel Wide');
    ds(
      p25,
      COL1_X,
      ROW1_Y,
      ITEM_W,
      ITEM_H,
      '1. Sales funnel (tailwind):',
      `- Awareness
- Interest
- Consideration
- Intent
- Purchase`,
      { type: 'funnel-wide', palette: 'tailwind' }
    );

    ds(
      p25,
      COL2_X,
      ROW1_Y,
      ITEM_W,
      ITEM_H,
      '2. Hiring pipeline (coolLight):',
      `- Sourced
- Applied
- Screened
- Interviewed
- Offered
- Hired`,
      { type: 'funnel-wide', palette: 'coolLight' }
    );

    ds(
      p25,
      COL1_X,
      ROW2_Y,
      ITEM_W,
      ITEM_H,
      '3. Learning stages (soft):',
      `- Exposure
- Familiarity
- Practice
- Mastery`,
      { type: 'funnel-wide', palette: 'soft' }
    );

    ds(
      p25,
      COL2_X,
      ROW2_Y,
      ITEM_W,
      ITEM_H,
      '4. Bug lifecycle (deep):',
      `- Reported
- Triaged
- Assigned
- In Progress
- Resolved`,
      { type: 'funnel-wide', palette: 'deep' }
    );

    // ── Page 26: iceberg ─────────────────────────────────────────────────────
    const p26 = await pdf.addPage();
    title(p26, 'SmartArt — Iceberg');
    ds(
      p26,
      COL1_X,
      ROW1_Y,
      ITEM_W,
      ITEM_H,
      '1. Skill iceberg (coolLight):',
      `- Technical Skills
  - Problem Solving
  - Soft Skills
  - Mindset
  - Values
  - Motivation`,
      { type: 'iceberg', palette: 'coolLight' }
    );

    ds(
      p26,
      COL2_X,
      ROW1_Y,
      ITEM_W,
      ITEM_H,
      '2. Brand iceberg (tailwind):',
      `- Visual Identity
  - Core Values
  - Culture
  - Mission
  - History`,
      { type: 'iceberg', palette: 'tailwind' }
    );

    ds(
      p26,
      COL1_X,
      ROW2_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H,
      '3. Conflict iceberg (deep):',
      `- Stated Position
  - Underlying Interests
  - Emotional Needs
  - Core Beliefs
  - Past Experiences`,
      { type: 'iceberg', palette: 'deep' }
    );

    // ── Page 27: scales ──────────────────────────────────────────────────────
    const p27 = await pdf.addPage();
    title(p27, 'SmartArt — Scales');
    ds(
      p27,
      COL1_X,
      ROW1_Y,
      ITEM_W,
      ITEM_H,
      '1. Work-life balance (soft):',
      `- Work
  - Meetings
  - Projects
  - Admin
- Life
  - Family
  - Hobbies
  - Rest`,
      { type: 'scales', palette: 'soft' }
    );

    ds(
      p27,
      COL2_X,
      ROW1_Y,
      ITEM_W,
      ITEM_H,
      '2. Pros vs cons (tailwind):',
      `- Pros
  - Fast delivery
  - Low cost
  - Scalable
- Cons
  - Technical debt
  - Risk`,
      { type: 'scales', palette: 'tailwind' }
    );

    ds(
      p27,
      COL1_X,
      ROW2_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H,
      '3. Build vs buy (coolLight):',
      `- Build
  - Custom fit
  - IP ownership
  - Full control
  - Long term
- Buy
  - Fast launch
  - Proven
  - Support`,
      { type: 'scales', palette: 'coolLight' }
    );

    // ── Page 28: mountain ────────────────────────────────────────────────────
    const p28 = await pdf.addPage();
    title(p28, 'SmartArt — Mountain');
    ds(
      p28,
      COL1_X,
      ROW1_Y,
      ITEM_W,
      ITEM_H,
      '1. Career path (tailwind):',
      `- Junior
- Mid-level
- Senior
- Lead
- Principal`,
      { type: 'mountain', palette: 'tailwind' }
    );

    ds(
      p28,
      COL2_X,
      ROW1_Y,
      ITEM_W,
      ITEM_H,
      '2. Product journey (coolLight):',
      `- Idea
- Prototype
- MVP
- Launch
- Growth
- Maturity`,
      { type: 'mountain', palette: 'coolLight' }
    );

    ds(
      p28,
      COL1_X,
      ROW2_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H,
      '3. Learning climb (soft):',
      `- Curiosity
- Foundations
- Practice
- Projects
- Mastery
- Teaching`,
      { type: 'mountain', palette: 'soft' }
    );

    // ── Page 29: fan ─────────────────────────────────────────────────────────
    const p29 = await pdf.addPage();
    title(p29, 'SmartArt — Fan');
    ds(
      p29,
      COL1_X,
      ROW1_Y,
      ITEM_W,
      ITEM_H,
      '1. Subject areas (tailwind):',
      `- Math
- Science
- Language
- Art
- Sport`,
      { type: 'fan', palette: 'tailwind' }
    );

    ds(
      p29,
      COL2_X,
      ROW1_Y,
      ITEM_W,
      ITEM_H,
      '2. Team roles (coolLight):',
      `- Design
- Engineering
- Marketing
- Sales
- Operations
- Finance`,
      { type: 'fan', palette: 'coolLight' }
    );

    ds(
      p29,
      COL1_X,
      ROW2_Y,
      ITEM_W,
      ITEM_H,
      '3. Product pillars (soft):',
      `- Speed
- Quality
- Cost
- UX`,
      { type: 'fan', palette: 'soft' }
    );

    ds(
      p29,
      COL2_X,
      ROW2_Y,
      ITEM_W,
      ITEM_H,
      '4. Days of week (deep):',
      `- Mon
- Tue
- Wed
- Thu
- Fri
- Sat
- Sun`,
      { type: 'fan', palette: 'deep' }
    );

    // ── Page 30: periodic ────────────────────────────────────────────────────
    const p30 = await pdf.addPage();
    title(p30, 'SmartArt — Periodic');
    ds(
      p30,
      COL1_X,
      ROW1_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H,
      '1. Design elements (tailwind):',
      `- Color
  - Hue
  - Saturation
- Typography
  - Hierarchy
- Space
  - Whitespace
- Shape
  - Geometry
- Texture
- Line
- Form
- Value
  - Contrast
- Pattern`,
      { type: 'periodic', palette: 'tailwind' }
    );

    ds(
      p30,
      COL1_X,
      ROW2_Y,
      ITEM_W,
      ITEM_H,
      '2. Nutrients (coolLight):',
      `- Protein
  - Amino acids
- Carbs
  - Sugar
  - Fiber
- Fat
  - Omega 3
- Water
- Vitamins
- Minerals`,
      { type: 'periodic', palette: 'coolLight' }
    );

    ds(
      p30,
      COL2_X,
      ROW2_Y,
      ITEM_W,
      ITEM_H,
      '3. Skills (soft):',
      `- Leadership
- Communication
- Analysis
- Creativity
- Focus
- Empathy
- Planning
- Execution`,
      { type: 'periodic', palette: 'soft' }
    );

    // ── Page 31: cycle-refined ───────────────────────────────────────────────
    const p31 = await pdf.addPage();
    title(p31, 'SmartArt — Cycle Refined');
    ds(
      p31,
      COL1_X,
      ROW1_Y,
      ITEM_W,
      ITEM_H,
      '1. Agile sprint (tailwind):',
      `- Plan
- Build
- Test
- Review
- Retrospect`,
      { type: 'cycle-refined', palette: 'tailwind' }
    );

    ds(
      p31,
      COL2_X,
      ROW1_Y,
      ITEM_W,
      ITEM_H,
      '2. Water cycle (coolLight):',
      `- Evaporation
- Condensation
- Precipitation
- Collection`,
      { type: 'cycle-refined', palette: 'coolLight' }
    );

    ds(
      p31,
      COL1_X,
      ROW2_Y,
      ITEM_W,
      ITEM_H,
      '3. PDCA (soft):',
      `- Plan
- Do
- Check
- Act`,
      { type: 'cycle-refined', palette: 'soft' }
    );

    ds(
      p31,
      COL2_X,
      ROW2_Y,
      ITEM_W,
      ITEM_H,
      '4. Learning loop (deep):',
      `- Experience
- Reflect
- Conceptualize
- Experiment
- Apply`,
      { type: 'cycle-refined', palette: 'deep' }
    );

    // ── Page 32: spider-map ──────────────────────────────────────────────────
    const p32 = await pdf.addPage();
    title(p32, 'SmartArt — Spider Map');
    ds(
      p32,
      COL1_X,
      ROW1_Y,
      ITEM_W,
      ITEM_H,
      '1. Project factors (tailwind):',
      `- Project
  - Scope
    - Features
    - Goals
  - Team
    - Roles
    - Skills
  - Timeline
    - Milestones
  - Budget
    - Costs`,
      { type: 'spider-map', palette: 'tailwind' }
    );

    ds(
      p32,
      COL2_X,
      ROW1_Y,
      ITEM_W,
      ITEM_H,
      '2. Learning topics (coolLight):',
      `- Learning
  - Reading
    - Books
    - Articles
  - Practice
    - Projects
    - Exercises
  - Community
    - Meetups
  - Reflection
    - Journaling`,
      { type: 'spider-map', palette: 'coolLight' }
    );

    ds(
      p32,
      COL1_X,
      ROW2_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H,
      '3. Business ecosystem (soft):',
      `- Business
  - Customers
    - B2C
    - B2B
  - Products
    - Core
    - Add-ons
  - Partners
    - Tech
    - Distribution
  - Operations
    - People
    - Systems
  - Finance
    - Revenue
    - Costs`,
      { type: 'spider-map', palette: 'soft' }
    );

    // ── Page 33: KWL Chart ───────────────────────────────────────────────────
    const p33 = await pdf.addPage();
    title(p33, 'SmartArt — KWL Chart');

    ds(
      p33,
      COL1_X,
      ROW1_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H,
      '1. Photosynthesis KWL (tailwind):',
      `- What I Know
  - Plants need light
  - Leaves are green
  - Produces oxygen
- What I Want to Know
  - How does it work?
  - What molecules?
  - Why only plants?
- What I Learned
  - Chlorophyll absorbs light
  - CO2 + H2O → glucose
  - Happens in chloroplasts`,
      { type: 'kwl', palette: 'tailwind' }
    );

    ds(
      p33,
      COL1_X,
      ROW2_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H,
      '2. French Revolution KWL (coolLight):',
      `- What I Know
  - 1789
  - Guillotine
  - Louis XVI
- What I Want to Know
  - Causes?
  - Key events?
  - Aftermath?
- What I Learned
  - Inequality caused revolt
  - Estates system failed
  - Led to Napoleon`,
      { type: 'kwl', palette: 'coolLight' }
    );

    // ── Page 34: T-Chart ─────────────────────────────────────────────────────
    const p34 = await pdf.addPage();
    title(p34, 'SmartArt — T-Chart');

    ds(
      p34,
      COL1_X,
      ROW1_Y,
      ITEM_W,
      ITEM_H,
      '1. Remote vs Office (soft):',
      `- Remote Work
  - Flexible hours
  - No commute
  - Home environment
  - Less collaboration
- Office Work
  - Team proximity
  - Structured day
  - Networking
  - Less distraction`,
      { type: 't-chart', palette: 'soft' }
    );

    ds(
      p34,
      COL2_X,
      ROW1_Y,
      ITEM_W,
      ITEM_H,
      '2. Pros vs Cons (tailwind):',
      `- Advantages
  - Lower costs
  - Faster delivery
  - Scalable
  - Easy updates
- Disadvantages
  - Technical debt
  - Security risk
  - Dependency
  - Learning curve`,
      { type: 't-chart', palette: 'tailwind' }
    );

    ds(
      p34,
      COL1_X,
      ROW2_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H,
      '3. SQL vs NoSQL (deep):',
      `- SQL Databases
  - Structured schema
  - ACID transactions
  - Mature tooling
  - Vertical scaling
  - Complex joins
- NoSQL Databases
  - Flexible schema
  - Eventual consistency
  - High throughput
  - Horizontal scaling
  - Denormalized data`,
      { type: 't-chart', palette: 'deep' }
    );

    // ── Page 35: Plus-Minus ──────────────────────────────────────────────────
    const p35 = await pdf.addPage();
    title(p35, 'SmartArt — Plus-Minus');

    ds(
      p35,
      COL1_X,
      ROW1_Y,
      ITEM_W,
      ITEM_H,
      '1. Microservices (soft):',
      `- Benefits
  - Independent deploy
  - Technology choice
  - Better scaling
  - Fault isolation
- Drawbacks
  - Network overhead
  - Complex ops
  - Data consistency
  - More boilerplate`,
      { type: 'plus-minus', palette: 'soft' }
    );

    ds(
      p35,
      COL2_X,
      ROW1_Y,
      ITEM_W,
      ITEM_H,
      '2. Agile (tailwind):',
      `- Positives
  - Fast feedback
  - Adaptable
  - Team ownership
  - Frequent releases
- Negatives
  - Scope creep
  - Documentation gap
  - Hard to estimate
  - Needs trust`,
      { type: 'plus-minus', palette: 'tailwind' }
    );

    ds(
      p35,
      COL1_X,
      ROW2_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H,
      '3. AI in Education (coolLight):',
      `- Opportunities
  - Personalized learning
  - Instant feedback
  - Accessible 24/7
  - Reduces teacher burden
  - Adaptive difficulty
- Challenges
  - Data privacy
  - Bias in models
  - Over-reliance
  - Quality control
  - Digital divide`,
      { type: 'plus-minus', palette: 'coolLight' }
    );

    // ── Page 36: Frayer Model ────────────────────────────────────────────────
    const p36 = await pdf.addPage();
    title(p36, 'SmartArt — Frayer Model');

    ds(
      p36,
      COL1_X,
      ROW1_Y,
      ITEM_W,
      ITEM_H,
      '1. Democracy (tailwind):',
      `- Democracy
- Definition
  - Rule by the people
  - Elected representatives
  - Majority decisions
- Characteristics
  - Free elections
  - Freedom of speech
  - Rule of law
- Examples
  - USA
  - Germany
  - Japan
- Non-Examples
  - Dictatorship
  - Monarchy
  - Theocracy`,
      { type: 'frayer', palette: 'tailwind' }
    );

    ds(
      p36,
      COL2_X,
      ROW1_Y,
      ITEM_W,
      ITEM_H,
      '2. Photosynthesis (coolLight):',
      `- Photosynthesis
- Definition
  - Light to chemical energy
  - In chloroplasts
  - Produces glucose
- Characteristics
  - Needs sunlight
  - Requires chlorophyll
  - Releases oxygen
- Examples
  - Leaves in sunlight
  - Algae in water
- Non-Examples
  - Respiration
  - Fermentation`,
      { type: 'frayer', palette: 'coolLight' }
    );

    ds(
      p36,
      COL1_X,
      ROW2_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H,
      '3. Metaphor (soft):',
      `- Metaphor
- Definition
  - Direct comparison
  - Without like/as
  - Implied meaning
- Characteristics
  - Figurative language
  - Creates imagery
  - Two unlike things
  - Poetic device
- Examples
  - Life is a journey
  - Time is money
  - Heart of gold
- Non-Examples
  - Simile (uses like/as)
  - Literal statements
  - Analogies`,
      { type: 'frayer', palette: 'soft' }
    );

    // ── Page 37: Domino ──────────────────────────────────────────────────────
    const p37 = await pdf.addPage();
    title(p37, 'SmartArt — Domino');

    ds(
      p37,
      COL1_X,
      ROW1_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H,
      '1. Cause-effect chain (tailwind):',
      `- Deforestation
  - Less carbon capture
- CO2 rises
  - Greenhouse effect
- Temperature rises
  - Ice melts
- Sea level rises
  - Coastal flooding`,
      { type: 'domino', palette: 'tailwind' }
    );

    ds(
      p37,
      COL1_X,
      ROW2_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H,
      '2. Economic chain (coolLight):',
      `- Interest rates rise
  - Borrowing costs up
- Consumer spending falls
  - Companies earn less
- Businesses cut costs
  - Unemployment rises
- Demand drops`,
      { type: 'domino', palette: 'coolLight' }
    );

    // ── Page 38: Conveyor Belt ───────────────────────────────────────────────
    const p38 = await pdf.addPage();
    title(p38, 'SmartArt — Conveyor Belt');

    ds(
      p38,
      COL1_X,
      ROW1_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H,
      '1. Software delivery pipeline (tailwind):',
      `- Code
  - Write
  - Review
- Build
  - Compile
  - Package
- Test
  - Unit
  - Integration
- Deploy
  - Staging
  - Production
- Monitor
  - Alerts
  - Metrics`,
      { type: 'conveyor', palette: 'tailwind' }
    );

    ds(
      p38,
      COL1_X,
      ROW2_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H,
      '2. Manufacturing process (soft):',
      `- Raw materials
- Cut & shape
- Assemble
- Paint & finish
- Quality check
- Package
- Ship`,
      { type: 'conveyor', palette: 'soft' }
    );

    // ── Page 39: Solar System ────────────────────────────────────────────────
    const p39 = await pdf.addPage();
    title(p39, 'SmartArt — Solar System');

    ds(
      p39,
      COL1_X,
      ROW1_Y,
      ITEM_W,
      ITEM_H,
      '1. Brand ecosystem (tailwind):',
      `- Brand
- Product
  - Features
  - UX
- Marketing
  - Social
  - Ads
- Support
  - Docs
  - Help
- Community
  - Forum
  - Events`,
      { type: 'solar-system', palette: 'tailwind' }
    );

    ds(
      p39,
      COL2_X,
      ROW1_Y,
      ITEM_W,
      ITEM_H,
      '2. Learning ecosystem (coolLight):',
      `- Learner
- Content
  - Videos
  - Reading
- Practice
  - Projects
  - Exercises
- Feedback
  - Tests
  - Mentor
- Community
  - Peers
  - Forum`,
      { type: 'solar-system', palette: 'coolLight' }
    );

    ds(
      p39,
      COL1_X,
      ROW2_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H,
      '3. Tech stack (soft):',
      `- App
- Frontend
  - React
  - CSS
- Backend
  - API
  - Auth
- Database
  - Postgres
  - Cache
- DevOps
  - CI/CD
  - Cloud`,
      { type: 'solar-system', palette: 'soft' }
    );

    // ── Page 40: Bridge ──────────────────────────────────────────────────────
    const p40 = await pdf.addPage();
    title(p40, 'SmartArt — Bridge');

    ds(
      p40,
      COL1_X,
      ROW1_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H,
      '1. Problem → Solution bridge (tailwind):',
      `- Problem
  - Analyse root
- Hypothesis
  - Test ideas
- Solution
  - Implement fix
- Outcome
  - Measure results
- Learning`,
      { type: 'bridge', palette: 'tailwind' }
    );

    ds(
      p40,
      COL1_X,
      ROW2_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H,
      '2. Idea to product (coolLight):',
      `- Idea
  - Validate market
- Research
  - Define scope
- Design
  - Build prototype
- Development
  - Ship MVP
- Launch`,
      { type: 'bridge', palette: 'coolLight' }
    );

    // ── Page 41: Nested Target ───────────────────────────────────────────────
    const p41 = await pdf.addPage();
    title(p41, 'SmartArt — Nested Target');

    ds(
      p41,
      COL1_X,
      ROW1_Y,
      ITEM_W,
      ITEM_H,
      '1. MoSCoW (tailwind):',
      `- Won't Have
- Could Have
- Should Have
- Must Have`,
      { type: 'nested-target', palette: 'tailwind' }
    );

    ds(
      p41,
      COL2_X,
      ROW1_Y,
      ITEM_W,
      ITEM_H,
      '2. Market segments (coolLight):',
      `- General public
- Target market
- Serviceable segment
- Core customers`,
      { type: 'nested-target', palette: 'coolLight' }
    );

    ds(
      p41,
      COL1_X,
      ROW2_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H,
      '3. Priority layers (soft):',
      `- Long-term goals
- Medium-term goals
- Short-term goals
- Daily actions
- Right now`,
      { type: 'nested-target', palette: 'soft' }
    );

    // ── Page 42: Gear ────────────────────────────────────────────────────────
    const p42 = await pdf.addPage();
    title(p42, 'SmartArt — Gear');

    ds(
      p42,
      COL1_X,
      ROW1_Y,
      ITEM_W,
      ITEM_H,
      '1. Three drivers (tailwind):',
      `- People
  - Skills
  - Motivation
- Process
  - Systems
  - Workflow
- Technology
  - Tools
  - Data`,
      { type: 'gear', palette: 'tailwind' }
    );

    ds(
      p42,
      COL2_X,
      ROW1_Y,
      ITEM_W,
      ITEM_H,
      '2. Org alignment (coolLight):',
      `- Strategy
  - Vision
  - Goals
- Operations
  - Execution
  - Metrics
- Culture
  - Values
  - Norms`,
      { type: 'gear', palette: 'coolLight' }
    );

    ds(
      p42,
      COL1_X,
      ROW2_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H,
      '3. Product-Market-Ops (soft):',
      `- Product
  - Features
  - Roadmap
- Market
  - Customers
  - Competition
- Operations
  - Team
  - Finance`,
      { type: 'gear', palette: 'soft' }
    );

    // ── Page 43: Roadmap ─────────────────────────────────────────────────────
    const p43 = await pdf.addPage();
    title(p43, 'SmartArt — Roadmap');

    ds(
      p43,
      COL1_X,
      ROW1_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H,
      '1. Product roadmap (tailwind):',
      `- Discovery
  - User research
  - Competitor audit
- Foundation
  - Core architecture
  - Auth system
- Alpha
  - MVP features
  - Internal testing
- Beta
  - Public testing
  - Feedback loop
- Launch
  - Marketing campaign
  - Full release`,
      { type: 'roadmap', palette: 'tailwind' }
    );

    ds(
      p43,
      COL1_X,
      ROW2_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H,
      '2. Learning path (coolLight):',
      `- Fundamentals
  - Syntax & types
- Intermediate
  - Design patterns
- Advanced
  - Architecture
- Expert
  - Mentoring & OSS`,
      { type: 'roadmap', palette: 'coolLight' }
    );

    // ── Page 44: Waffle Chart ────────────────────────────────────────────────
    const p44 = await pdf.addPage();
    title(p44, 'SmartArt — Waffle Chart');

    ds(
      p44,
      COL1_X,
      ROW1_Y,
      ITEM_W,
      ITEM_H,
      '1. Budget split (tailwind):',
      `- Engineering:45
- Marketing:25
- Operations:20
- Other:10`,
      { type: 'waffle', palette: 'tailwind' }
    );

    ds(
      p44,
      COL2_X,
      ROW1_Y,
      ITEM_W,
      ITEM_H,
      '2. Traffic sources (coolLight):',
      `- Organic:55
- Paid:20
- Direct:15
- Referral:10`,
      { type: 'waffle', palette: 'coolLight' }
    );

    ds(
      p44,
      COL1_X,
      ROW2_Y,
      ITEM_W,
      ITEM_H,
      '3. Time allocation (soft):',
      `- Feature work:40
- Bugs:25
- Meetings:20
- Docs:15`,
      { type: 'waffle', palette: 'soft' }
    );

    ds(
      p44,
      COL2_X,
      ROW2_Y,
      ITEM_W,
      ITEM_H,
      '4. Skill breakdown (deep):',
      `- Frontend:35
- Backend:30
- DevOps:20
- Design:15`,
      { type: 'waffle', palette: 'deep' }
    );

    // ── Page 45: Stacked Blocks ──────────────────────────────────────────────
    const p45 = await pdf.addPage();
    title(p45, 'SmartArt — Stacked Blocks');

    ds(
      p45,
      COL1_X,
      ROW1_Y,
      ITEM_W,
      ITEM_H,
      '1. Tech stack layers (tailwind):',
      `- Presentation
  - React
  - CSS
  - Icons
- Business Logic
  - Services
  - Rules
  - Validation
- Data Access
  - ORM
  - Cache
  - Migrations
- Infrastructure
  - Cloud
  - Docker
  - CI/CD`,
      { type: 'stacked-blocks', palette: 'tailwind' }
    );

    ds(
      p45,
      COL2_X,
      ROW1_Y,
      ITEM_W,
      ITEM_H,
      '2. Org levels (coolLight):',
      `- Executive
  - CEO
  - CTO
  - CFO
- Management
  - Directors
  - Managers
- Individual Contributors
  - Engineers
  - Designers
- Support
  - Ops
  - HR`,
      { type: 'stacked-blocks', palette: 'coolLight' }
    );

    ds(
      p45,
      COL1_X,
      ROW2_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H,
      '3. Learning levels (soft):',
      `- Awareness
  - Exposure
  - Overview
- Knowledge
  - Concepts
  - Theory
- Skill
  - Practice
  - Projects
- Mastery
  - Teach others
  - Innovate`,
      { type: 'stacked-blocks', palette: 'soft' }
    );

    // ── Page 46: Linear Venn ─────────────────────────────────────────────────
    const p46 = await pdf.addPage();
    title(p46, 'SmartArt — Linear Venn');

    ds(
      p46,
      COL1_X,
      ROW1_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H,
      '1. Data science skills (tailwind):',
      `- Statistics
  - Probability
  - Distributions
  - Inference
- Data Science
  - ML models
  - Pipelines
  - Analysis
- Engineering
  - Systems
  - Deployment
  - Scaling`,
      { type: 'linear-venn', palette: 'tailwind' }
    );

    ds(
      p46,
      COL1_X,
      ROW2_Y,
      ITEM_W * 2 + MM(12),
      ITEM_H,
      '2. Design process (coolLight):',
      `- Research
  - Interviews
  - Analytics
  - Surveys
- Design
  - Ideation
  - Prototyping
  - Iteration
- Development
  - Implementation
  - Testing
  - Delivery`,
      { type: 'linear-venn', palette: 'coolLight' }
    );
  }
}
